import { API_ENDPOINT } from "@/constants/appConstants";
import {
  APPLIED_JOB_STATUS_QUERY_KEY,
  SIMILAR_JOBS_QUERY_KEY,
} from "@/lib/queryKeys";
import { UseQueryResult, useQuery } from "@tanstack/react-query";

export interface Job {
  applicationData: any;
  jobDetails: any;
}

export interface JobMetaData {
  paging: Object;
  resultId: string;
}

export interface SimilarJobsResponse {
  data: Object[];
  meta: JobMetaData[];
  filters: any[];
}

export interface JobStatusResponse {
  applicationHistory: { uuid: string; text: string; statusUpdatedAt: number }[];
}

export interface PrepAIQuestion {
  question: string;
  answer: string;
}

export interface PrepAIQuestionsResponse {
  questions: PrepAIQuestion[];
}

export const jobStatusAPI = async <T>(
  applicationId?: string,
): Promise<T | Error> => {
  try {
    const host = window.location.host;
    const response = await fetch(
      `${API_ENDPOINT(host).middleware.getJobStatus}?applicationId=${applicationId}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    const data: T = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(
        `Error occurred while fetching data, ${JSON.stringify(error)}`,
      );
    }
  }
};

export const similarJobsAPI = async (
  jobId?: string,
  loggedInUser: boolean = true,
  experience: number = 0,
): Promise<SimilarJobsResponse> => {
  try {
    const host = window.location.host;
    const response = await fetch(
      `${API_ENDPOINT(host).middleware.getSimilarJobs}?jobId=${jobId}&loggedInUser=${loggedInUser}&experience=${experience}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    const data: SimilarJobsResponse = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(
        `Error occurred while fetching data, ${JSON.stringify(error)}`,
      );
    }
  }
};

export const jobSaveUnsaveAPI = async <T>(
  jobId?: number,
  type?: string,
): Promise<T | Error> => {
  try {
    const host = window.location.host;

    const response = await fetch(
      `${API_ENDPOINT(host).middleware.jobSaveUnsaveAPI}?type=${type}`,
      {
        method: type == "save" ? "POST" : "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          jobId: jobId,
        }),
      },
    );

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(
        `Error occurred while saving this job, ${JSON.stringify(error)}`,
      );
    }
  }
};

export const prepAIQuestionsAPI = async <T>(
  jobId?: number,
): Promise<T | Error> => {
  try {
    const host = window.location.host;

    const response = await fetch(
      `${API_ENDPOINT(host).middleware.getPrepAIQuestions}`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          jobId: jobId,
        }),
      },
    );

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(
        `Error occurred while fetching prepAI questions, ${JSON.stringify(error)}`,
      );
    }
  }
};

export const useJobStatusAndActivity = (
  applicationId: string,
  enableFetch: boolean,
): UseQueryResult<JobStatusResponse> => {
  return useQuery({
    queryKey: [APPLIED_JOB_STATUS_QUERY_KEY, applicationId, enableFetch],
    queryFn: () => jobStatusAPI<JobStatusResponse>(applicationId),
    enabled: enableFetch,
  });
};

export const useSimilarJobs = (
  jobId: string,
  loggedInUser: boolean,
  experience: number,
  enableFetch: boolean,
): UseQueryResult<SimilarJobsResponse> => {
  return useQuery({
    queryKey: [
      SIMILAR_JOBS_QUERY_KEY,
      jobId,
      loggedInUser,
      experience,
      enableFetch,
    ],
    queryFn: () => similarJobsAPI(jobId, loggedInUser, experience),
    enabled: enableFetch,
  });
};
